import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { DrinkOrderDialogData, DrinkOrderDialogResult } from '@posModels';
import { DrinkIngredientModification } from '@posTypes';
import { QTY_MIN, QTY_STEP_PIECE, QTY_STEP_VOLUME } from './drink-order-dialog.constants';

@Component({
  selector: 'app-drink-order-dialog',
  imports: [ DecimalPipe, MatDialogModule, TranslateModule ],
  templateUrl: './drink-order-dialog.component.html',
  styleUrl: './drink-order-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrinkOrderDialogComponent {
  private dialogRef = inject(MatDialogRef<DrinkOrderDialogComponent>);
  private data = inject<DrinkOrderDialogData>(MAT_DIALOG_DATA);

  readonly product = this.data.product;

  ingredients = signal<DrinkIngredientModification[]>(
    (this.data.product.recipe ?? [])
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(item => ({
        ingredientId: item.ingredientId,
        ingredientName: item.ingredientName,
        defaultQty: item.qty,
        actualQty: item.qty,
        unitPriceCents: item.ingredientPriceCents,
        volumeMl: item.ingredientVolumeMl ?? null,
      })),
  );

  calculatedPrice = computed(() => {
    const base = this.product.extraCents ?? 0;
    return base + this.ingredients().reduce((sum, ing) => sum + ing.actualQty * ing.unitPriceCents, 0);
  });

  modificationNote = computed(() => {
    const modified = this.ingredients().filter(ing => ing.actualQty !== ing.defaultQty);
    if (modified.length === 0) {
      return '';
    }
    const parts = modified.map(ing => `${ ing.actualQty }× ${ ing.ingredientName }`);
    return `${ this.product.name } (${ parts.join(', ') })`;
  });

  adjustQty(index: number, delta: number): void {
    this.ingredients.update(items => {
      const updated = [ ...items ];
      const item = updated[index];
      const step = item.volumeMl != null ? QTY_STEP_VOLUME : QTY_STEP_PIECE;
      const newQty = Math.max(QTY_MIN, Math.round((item.actualQty + delta * step) * 10) / 10);
      updated[index] = { ...item, actualQty: newQty };
      return updated;
    });
  }

  confirm(): void {
    const result: DrinkOrderDialogResult = {
      ingredientModifications: this.ingredients(),
      calculatedPriceCents: this.calculatedPrice(),
      modificationNote: this.modificationNote(),
    };
    this.dialogRef.close(result);
  }

  close(): void {
    this.dialogRef.close();
  }
}
